import type { SupportCenterVolumeMetrics } from '../../types/supportCenterReports'
import { emptyStrategicMetrics } from '../../utils/supportCenterStrategicMetrics'
import {
  SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS,
  type SupportCenterAppointmentChannel,
} from '../../types/supportCenterAppointments'
import type { SupportCenterAppointmentStatus } from '../../types/supportCenterTriage'
import { ymdInSaoPaulo } from '../../utils/dateOnly'

export const DASHBOARD_INPUT_CLASS =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

/** Controles compactos do filtro de período (sem w-full). */
export const DASHBOARD_DATE_CONTROL_CLASS =
  'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

export const DASHBOARD_CHART_COLORS = [
  '#008B95',
  '#0d9488',
  '#f59e0b',
  '#ef4444',
  '#6366f1',
  '#64748b',
]

export const DASHBOARD_TOOLTIP_CONTENT_STYLE = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
}

export const DASHBOARD_TOOLTIP_LABEL_STYLE = {
  color: '#111827',
  fontWeight: 600,
}

export const DASHBOARD_TOOLTIP_ITEM_STYLE = {
  color: '#111827',
}

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

export function formatCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

export function patientKinshipLabel(row: {
  holder_cpf?: string | null
  patient_cpf?: string | null
  relationship_to_holder?: string | null
}): string | null {
  const holder = onlyDigits(row.holder_cpf ?? '')
  const patient = onlyDigits(row.patient_cpf ?? '')
  if (holder && patient && holder === patient) return 'Titular'
  const kinship = row.relationship_to_holder?.trim()
  return kinship || null
}

export function formatPhone(value: string | null | undefined): string {
  let digits = onlyDigits(value ?? '')
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    digits = digits.slice(2)
  }
  digits = digits.slice(0, 11)
  if (!digits) return ''
  if (digits.length <= 2) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

export function phoneTelHref(value: string | null | undefined): string | null {
  const digits = onlyDigits(value ?? '')
  if (digits.length < 10) return null
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return `tel:+${digits}`
  }
  if (digits.length === 10 || digits.length === 11) {
    return `tel:+55${digits}`
  }
  return `tel:${digits}`
}

export function formatDateTime(value: string): string {
  if (!value) return '—'
  return new Date(value.includes('T') ? value : value.replace(' ', 'T')).toLocaleString('pt-BR')
}

/** Data e hora em linhas separadas (ex.: lista de triagem). */
export function formatDateTimeLines(value: string): { date: string; time: string } | null {
  if (!value) return null
  const formatted = formatDateTime(value)
  if (formatted === '—') return null
  const comma = formatted.indexOf(',')
  if (comma < 0) return { date: formatted, time: '' }
  return {
    date: formatted.slice(0, comma).trim(),
    time: formatted.slice(comma + 1).trim(),
  }
}

export function formatDateBr(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(isoDate)) return isoDate
  const [y, m, d] = isoDate.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

export function todayIso(): string {
  return ymdInSaoPaulo()
}

export function daysAgoIso(days: number): string {
  return ymdInSaoPaulo(new Date(Date.now() - days * 24 * 60 * 60 * 1000))
}

/** Tipo do seletor de data do dashboard (padrão CCT: Data / Mês / Ano). */
export type DashboardDateFilterKind = 'data' | 'mes' | 'ano'

export type DashboardDateStartOperator = 'igual' | 'a_partir_de'
export type DashboardDateEndOperator = 'igual' | 'ate'

/** Converte valor do filtro de início (data/mês/ano) em ISO yyyy-mm-dd (início do período). */
export function resolveDashboardDateFrom(
  value: string,
  kind: DashboardDateFilterKind
): string {
  const v = value.trim()
  if (!v) return ''
  if (kind === 'data' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v
  if (kind === 'mes' && /^\d{4}-\d{2}$/.test(v)) return `${v}-01`
  if (kind === 'ano' && /^\d{4}$/.test(v)) return `${v}-01-01`
  return ''
}

/** Converte valor do filtro de fim (data/mês/ano) em ISO yyyy-mm-dd (fim do período). */
export function resolveDashboardDateTo(
  value: string,
  kind: DashboardDateFilterKind
): string {
  const v = value.trim()
  if (!v) return ''
  if (kind === 'data' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v
  if (kind === 'mes' && /^\d{4}-\d{2}$/.test(v)) {
    const [y, m] = v.split('-').map(Number)
    const last = new Date(y, m, 0).getDate()
    return `${v}-${String(last).padStart(2, '0')}`
  }
  if (kind === 'ano' && /^\d{4}$/.test(v)) return `${v}-12-31`
  return ''
}

/** Converte o valor ao trocar Data / Mês / Ano, preservando o período quando possível. */
export function convertDashboardDateFilterValue(
  value: string,
  fromKind: DashboardDateFilterKind,
  toKind: DashboardDateFilterKind
): string {
  if (fromKind === toKind) return value
  const iso = resolveDashboardDateFrom(value, fromKind)
  if (!iso) return ''
  if (toKind === 'data') return iso
  if (toKind === 'mes') return iso.slice(0, 7)
  return iso.slice(0, 4)
}

/**
 * Resolve o intervalo aplicado a partir dos controles Início / Fim do período.
 * `igual` sozinho colapsa para o bound daquele lado; com os dois lados preenchidos, compõe o range.
 * Campos vazios não aplicam limite naquele lado.
 */
export function resolveDashboardDateRange(input: {
  startValue: string
  startKind: DashboardDateFilterKind
  startOperator: DashboardDateStartOperator
  endValue: string
  endKind: DashboardDateFilterKind
  endOperator: DashboardDateEndOperator
}): { dateFrom: string; dateTo: string } {
  const startFrom = resolveDashboardDateFrom(input.startValue, input.startKind)
  const startTo = resolveDashboardDateTo(input.startValue, input.startKind)
  const endFrom = resolveDashboardDateFrom(input.endValue, input.endKind)
  const endTo = resolveDashboardDateTo(input.endValue, input.endKind)
  const hasStart = Boolean(startFrom && startTo)
  const hasEnd = Boolean(endFrom && endTo)

  if (input.startOperator === 'igual' && hasStart && !hasEnd) {
    return { dateFrom: startFrom, dateTo: startTo }
  }
  if (input.endOperator === 'igual' && hasEnd && !hasStart) {
    return { dateFrom: endFrom, dateTo: endTo }
  }

  return {
    dateFrom: startFrom,
    dateTo: endTo,
  }
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  const maybe = error as { message?: string; code?: string }
  if (maybe?.message) return maybe.message
  if (maybe?.code) return `Erro (${maybe.code})`
  return 'Erro inesperado ao processar solicitação.'
}

export function appointmentStatusBadgeClass(status: SupportCenterAppointmentStatus): string {
  if (status === 'marcado') {
    return 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300'
  }
  if (status === 'reagendado') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  }
  if (status === 'cancelado') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  }
  if (status === 'realizado') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  }
  return 'bg-slate-100 text-slate-800 dark:bg-slate-800/50 dark:text-slate-300'
}

/** Status visual do formulário de atendimento (agenda / pendentes). */
export type AttendanceFormStatusKind = 'concluido' | 'pendente' | 'rascunho' | 'sem_rascunho'

export const ATTENDANCE_FORM_STATUS_LABELS: Record<AttendanceFormStatusKind, string> = {
  concluido: 'Concluído',
  pendente: 'Pendente',
  rascunho: 'Rascunho',
  sem_rascunho: 'Sem rascunho',
}

export function attendanceFormStatusBadgeClass(kind: AttendanceFormStatusKind): string {
  if (kind === 'concluido') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  }
  if (kind === 'rascunho') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  }
  // pendente | sem_rascunho
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
}

/** null = atendimento ainda não iniciado (exibir "—"). */
export function attendanceFormStatusFromEvent(event: {
  attendance_form_submitted?: boolean | null
  attendance_started_at?: string | null
}): Exclude<AttendanceFormStatusKind, 'rascunho' | 'sem_rascunho'> | null {
  if (event.attendance_form_submitted) return 'concluido'
  if (event.attendance_started_at?.trim()) return 'pendente'
  return null
}

export function attendanceFormDraftKind(hasDraft: boolean): 'rascunho' | 'sem_rascunho' {
  return hasDraft ? 'rascunho' : 'sem_rascunho'
}

export function emptyVolumeMetrics(): SupportCenterVolumeMetrics {
  return {
    total: 0,
    by_status: {
      marcado: 0,
      reagendado: 0,
      cancelado: 0,
      realizado: 0,
      nao_realizado: 0,
      pre_agendado: 0,
    },
    cancel_rate: 0,
    by_specialization: [],
    by_day: [],
    by_day_by_specialization: [],
    by_kinship: [],
    by_client: [],
    by_broker: [],
    by_channel: (
      ['whatsapp', 'telefone', 'videochamada', 'whatsapp-bot'] as SupportCenterAppointmentChannel[]
    ).map((channel) => ({
      channel,
      name: SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS[channel],
      total: 0,
    })),
    attendance_by_demand_type: [],
    attendance_by_demand_type_by_specialization: [],
    triage_by_status: { pendente: 0, concluida: 0, reprovado: 0, cancelado: 0, nao_realizado: 0 },
    triage_by_specialization: [],
    triage_by_day: [],
    triage_by_referral_origin: [],
    triage_by_demand_type: [],
    triage_by_demand_type_by_specialization: [],
    strategic: emptyStrategicMetrics(),
  }
}
